import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACGroup, RoleGroup } from '../../../types';
import StagedCountBadges from '../../roles/components/StagedCountBadges';
import {
  selectPendingRoleAdds,
  selectPendingRoleDeletes,
  selectSelectedRoleGroupId,
  selectRole,
  addRoleToPendingAdds,
  addRoleToPendingDeletes,
} from '../services/roleMembershipSlice';

import Autocomplete from './GroupAutoComplete2';
import GroupDisplayItem from './GroupDisplayItem';

type Props = {
  groups?: RBACGroup[];
  // roleId -> direct group(s) that confer it (for inherited roles)
  inheritance?: Record<string, RBACGroup[]>;
  // All role groups available to add, shown in the in-panel "add a role" search box.
  roleOptions?: RBACGroup[];
  // Whether a user is selected — enables the add box.
  enabled?: boolean;
};

export default function RoleGroupList({ groups, inheritance, roleOptions, enabled }: Props) {
  const pendingAdds = useAppSelector(selectPendingRoleAdds);
  const pendingDeletes = useAppSelector(selectPendingRoleDeletes);
  const selectedGroupId = useAppSelector(selectSelectedRoleGroupId);
  const dispatch = useAppDispatch();

  const isRoleGroup = (group: RBACGroup): group is RoleGroup => {
    return 'transitive' in group;
  };

  if (!groups) {
    return <div></div>;
  }

  // Roles offered in the add box: everything the user doesn't already hold (direct or
  // inherited) and hasn't already queued — picking one of those would be a no-op.
  const existingIds = new Set([...groups, ...pendingAdds].map((group) => group.id));
  const addCandidates = (roleOptions ?? []).filter(
    (group) => group.displayName && group.id && !existingIds.has(group.id)
  );

  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col flex-1 min-h-0">
      <div className="flex flex-row items-center justify-between gap-2 pb-2">
        <div className="flex flex-row items-center gap-2 min-w-0">
          <span className="font-bold truncate">Role membership</span>
          <span className="badge badge-ghost shrink-0" title="Current role memberships">
            {groups.length}
          </span>
          <div className="shrink-0">
            <StagedCountBadges addCount={pendingAdds.length} deleteCount={pendingDeletes.length} />
          </div>
        </div>
        <div className="w-64 shrink-0">
          <Autocomplete
            items={addCandidates}
            onSelect={(group) => dispatch(addRoleToPendingAdds(group))}
            enabled={enabled}
            clearOnSelect
            placeholder="Add a role"
          />
        </div>
      </div>
      {pendingAdds.length + groups.length === 0 ? (
        <div className="text-sm opacity-60">No role memberships.</div>
      ) : (
        <div className="flex flex-row border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
          <ul className="divide-y divide-base-200 w-full h-full overflow-y-auto">
            {
              // Concatenate pendingAdds and pendingDeletes to get the full list of groups to display
              [...pendingAdds, ...groups].map((group: RBACGroup) => {
                if (isRoleGroup(group)) {
                  const pendingAdd = pendingAdds.indexOf(group) !== -1;
                  const pendingDelete = pendingDeletes.some((g) => g.id === group.id);
                  const key = pendingAdd || pendingDelete ? group.id + 'pending' : group.id;
                  const derivedFrom = group.transitive
                    ? inheritance?.[group.id!]?.map((g) => g.displayName).join(', ')
                    : undefined;
                  return (
                    <GroupDisplayItem
                      key={key}
                      displayName={group.displayName!}
                      roleId={group.id!}
                      selected={selectedGroupId === group.id}
                      transitive={group.transitive}
                      derivedFrom={derivedFrom}
                      added={pendingAdd}
                      onClick={() => {
                        dispatch(selectRole(group));
                      }}
                      onDelete={() => {
                        dispatch(addRoleToPendingDeletes(group));
                      }}
                      deleted={pendingDelete}
                    />
                  );
                }
              })
            }
          </ul>
        </div>
      )}
    </div>
  );
}
